/**
 * BOSS Reports — where the shortcut panel and the field editor live, and whether they are showing.
 *
 * Both panels are useful and both cost space the pivot grid wants. The shortcut panel holds a
 * 260px column for something you touch once a session; the field editor sits above the grid and
 * grows as it restacks, to the point where it once took the whole pane (see `.boss-fieldbar`).
 * So each can be popped out of the layout into a floating panel over the grid, and each can be
 * hidden outright — while the report's own title and buttons stay put, because the float layer
 * starts below the toolbar.
 *
 * Two independent axes per panel, both remembered:
 *   mode    'docked'  the panel occupies layout space, as it always has
 *           'popup'   the panel floats over the grid, which takes the space it vacated
 *   visible whether it is on screen at all
 *
 * They are independent on purpose. "Popped out but hidden" is the state that actually buys the
 * grid the whole pane, and it is the one a heavy grid user wants most of the time; "docked but
 * hidden" is the same idea for someone who otherwise likes the docked layout.
 */
import { useCallback, useEffect, useState } from 'react'

export type BossPanelMode = 'docked' | 'popup'

export type BossPanelState = {
    mode: BossPanelMode
    visible: boolean
}

/** The two panels this applies to. */
export type BossPanelKey = 'shortcuts' | 'fields'

export type BossPanelsState = Record<BossPanelKey, BossPanelState>

/** Docked and showing — what the page did before any of this existed. */
export const DEFAULT_PANELS: BossPanelsState = {
    shortcuts: { mode: 'docked', visible: true },
    fields: { mode: 'docked', visible: true },
}

const STORAGE_KEY = 'boss-panels'

const PANEL_KEYS: BossPanelKey[] = ['shortcuts', 'fields']

/**
 * Narrows whatever is in storage to a usable state, falling back per FIELD rather than per panel
 * or wholesale. A half-written or older value should cost the user the one setting it got wrong,
 * not put both panels back to the default.
 */
export function parsePanels(raw: string | null): BossPanelsState {
    if (raw === null) return DEFAULT_PANELS

    let parsed: unknown
    try {
        parsed = JSON.parse(raw)
    } catch {
        return DEFAULT_PANELS
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return DEFAULT_PANELS

    const source = parsed as Record<string, unknown>
    const next = {} as BossPanelsState
    for (const key of PANEL_KEYS) {
        const entry = source[key]
        const fallback = DEFAULT_PANELS[key]
        if (typeof entry !== 'object' || entry === null || Array.isArray(entry)) {
            next[key] = fallback
            continue
        }
        const { mode, visible } = entry as Record<string, unknown>
        next[key] = {
            mode: mode === 'popup' || mode === 'docked' ? mode : fallback.mode,
            visible: typeof visible === 'boolean' ? visible : fallback.visible,
        }
    }
    return next
}

/**
 * Reads once on mount rather than in a `useState` initialiser.
 *
 * Storage can be unavailable or throw outright (a private window, blocked site data), and it is
 * per-browser, so the value is a convenience and never something the page depends on. Doing it in
 * an effect also keeps the first render identical for every visitor, which is what stops the
 * panels flickering from the default position to the stored one.
 */
export function useBossPanels(): {
    panels: BossPanelsState
    /** Show or hide one panel. */
    setVisible: (key: BossPanelKey, visible: boolean) => void
    /** Move one panel between the layout and a floating panel over the grid. */
    setMode: (key: BossPanelKey, mode: BossPanelMode) => void
} {
    const [panels, setPanels] = useState<BossPanelsState>(DEFAULT_PANELS)
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
        try {
            setPanels(parsePanels(window.localStorage.getItem(STORAGE_KEY)))
        } catch {
            // Storage is a convenience; the defaults are a perfectly good page.
        }
        setLoaded(true)
    }, [])

    useEffect(() => {
        // Not before the read above has run, or the first render would write the defaults over
        // whatever the user had chosen.
        if (!loaded) return
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(panels))
        } catch {
            // Full, or blocked. The choice still holds for this page view.
        }
    }, [panels, loaded])

    const setVisible = useCallback((key: BossPanelKey, visible: boolean) => {
        setPanels(current => (current[key].visible === visible
            ? current
            : { ...current, [key]: { ...current[key], visible } }))
    }, [])

    const setMode = useCallback((key: BossPanelKey, mode: BossPanelMode) => {
        setPanels(current => (current[key].mode === mode
            ? current
            // Moving a panel is also a request to look at it: docking a hidden panel that then
            // stayed hidden would read as the control doing nothing at all.
            : { ...current, [key]: { mode, visible: true } }))
    }, [])

    return { panels, setVisible, setMode }
}
